/**
 * Socket.IO 事件契约的**唯一权威出处（TypeScript 侧）**（Phase 5 Task 9；契约 §19，计划 §4.2/§4.4）。
 *
 * 另一侧是机器可读的 `.sisyphus/verify/socket-contract.json`（**要提交**）：
 * 网关（`apps/api`）、收银端（`apps/tenant`）、门禁与验收脚本都读这两处之一，不得各写一份。
 * `packages/types/src/realtime.spec.ts` 把两边**逐字**钉在一起（事件名、控制事件、房间前缀、
 * 载荷键集、重连参数、握手字段），并且是双向的：改本文件会红，改 JSON 也会红。
 *
 * 三条发射纪律（契约 §19.3 / 计划 §4.2，网关侧实现，写在这里因为类型是两端共同的读法）：
 *   ① **先落库、后推送**：事件在事务**提交之后**发射；事务内发射会产生「推送成功但回滚」的幽灵卡；
 *   ② **房间由服务端从库里读出的 `locationId` 决定**，不接受客户端指定（见 `locationRoom`）；
 *   ③ **幂等消费**：客户端以 `orderId` 为键后写覆盖，不做增量累加 —— 重复或乱序到达不产生副作用。
 */
/**
 * 8 个 S→C 事件名（契约 §19.3 的清单，顺序即冻结接口的一部分）。
 *
 * `as const` 是**要求**而不是风格：`RealtimeEvent` 必须是字面量联合，网关的 `emit` 与收银端的
 * `switch` 才能靠它做穷尽性检查（`const _never: never = event`）。若写成 `string[]`，
 * 事件的漏接与拼写错误会退化成运行时静默丢弃。
 */
export const REALTIME_EVENTS = [
    'order:new',
    'order:accepted',
    'order:pick-progress',
    'order:paid-confirmed',
    'order:ready',
    'order:cancelled',
    'order:completed',
    'stock:changed',
];
/**
 * C→S 控制事件：连接成功后客户端发送 `join`（契约 §19.2），载荷
 * `{ locationId: string, lastEventAt?: string }`（`lastEventAt` 见 §19.6 重连补发）。
 */
export const JOIN_EVENT = 'join';
/**
 * S→C 控制事件：服务端对 `join` 的回执（契约 §19.2 / §19.6），载荷 `JoinedPayload`。
 * 计划 §4.2：`joined` 是**单播**（发到 `cashier:<deviceId>`），而不是广播到 `location:<id>` ——
 * 重连补发只对重连的那台设备有意义。
 */
export const JOINED_EVENT = 'joined';
/** Socket.IO 端点路径（契约 §19.1：`wss://<host>/socket.io`，命名空间 `/`） */
export const SOCKET_PATH = '/socket.io';
/**
 * 握手 `auth` 的字段集（契约 §19.1）：`token`（accessToken）· `deviceId`（收银端唯一标识，
 * 持久化在 localStorage，同时是 `cashier:<deviceId>` 房间的键）· `clientType`（服务端据此区分
 * CASHIER / CUSTOMER）。
 *
 * 服务端校验顺序：解析 JWT → 校验租户状态与成员状态 → 校验该成员对目标地点有 `order:accept`
 * 权限 → 拒绝则断开并发送 `connect_error`（`data.code` 为 `AUTH_*`）。
 */
export const HANDSHAKE_AUTH_FIELDS = ['token', 'deviceId', 'clientType'];
/** 握手 `clientType` 的收银端取值（本 Phase 只实现 `CASHIER`；顾客会话走 REST，不上 Socket.IO） */
export const CASHIER_CLIENT_TYPE = 'CASHIER';
/**
 * 重连补发的订单条数上限（契约 §19.6 / 计划 §4.2）：服务端返回 `lastEventAt` 之后有状态变更的
 * 订单，**最多 50 条、按 `updatedAt` 升序**。上限存在的理由是「离线一整天的收银端不能把队列
 * 一次性灌爆」—— 超出部分靠随后的轮询/快照补齐，而不是让它悄悄漏掉。
 */
export const RECONNECT_MAX_CHANGED_ORDERS = 50;
/** 客户端在 `join` 里携带的补发起点字段名（契约 §19.6：本地记录的最近一次事件时间，localStorage 持久化） */
export const RECONNECT_JOIN_FIELD = 'lastEventAt';
/**
 * `location:<locationId>`：该地点全部收银端 + 该地点的顾客会话（契约 §19.2）。
 *
 * **房间由服务端从库里读出的 `locationId` 决定，不接受客户端指定**（计划 §4.2 发射纪律 ②）：
 * `join` 载荷里的 `locationId` 只用于**校验**（该成员对该地点有 `order:accept` 权限），
 * 房间归属以服务端读库结果为准 —— 否则任何登录用户都能订阅他人地点的订单流。
 *
 * 房间名的拼接只在这里实现一处：网关、收银端、验收脚本都调它，不写字面量。
 */
export function locationRoom(locationId) {
    return `location:${locationId}`;
}
/**
 * `cashier:<deviceId>`：单个收银设备（`deviceId` 来自握手 `auth.deviceId`），
 * 用于定向回执 —— `joined` 补发只发给重连的那台设备（计划 §4.2）。
 */
export function cashierRoom(deviceId) {
    return `cashier:${deviceId}`;
}
/**
 * `tenant:<tenantId>`：社团级房间。
 *
 * 契约 §19.2 的房间表只列了 `location:` 与 `cashier:`，这个函数存在是因为**租户隔离是硬要求**
 * （契约 §21.3）：跨地点的社团级事件（如权限/成员变化）必须有一个按租户收口的房间，
 * 而不是发到某个地点的房间再让收银端各自过滤。同样由服务端从库里读出的 `tenantId` 决定。
 */
export function tenantRoom(tenantId) {
    return `tenant:${tenantId}`;
}
/** 穷尽性守卫：`switch` 的 `default` 分支里调它，漏掉事件名即为编译错误 */
export function assertNever(value) {
    throw new Error(`未处理的事件：${JSON.stringify(value)}`);
}
//# sourceMappingURL=realtime.js.map